<template>
	<div class="workflow-status" :class="{ fixed: fixedPosition }" v-show="state.isShow">
		<div v-if="showSimpleLoader" />
		<div v-show="!showSimpleLoader" class="progress_area">
			<ProgressBar
				v-if="!isError"
				ref="progressBar"
				class="progress_wrp"
				:allowMinimize="allowMinimize"
				:allowRestart="allowRestart"
				:disableRestart="disableRestart"
				:allowStop="allowStop"
				:disableStop="disableStop"
				:showPercentage="showProgressBar"
				:nodeRuns="state.runState.nodeRuns || []"
				:percentCompleted="String(state.percentCompleted)"
				:startMinimized="startMinimized"
				:text="state.text"
				@close="$emit('close')"
				@restart="$emit('restart')"
				@stop="$emit('stop')"
			/>
			<NonExistantToolError v-else-if="showNonExistantToolError" />
			<div v-else class="progress_wrp pb_show_error_msg">
				<div class="err">
					<div class="ply_error_cont">
						<div class="logo">
							<PlyLogoWithTextSVG />
						</div>
						<div class="error_msg_title">Something went wrong</div>
						<p class="font-weight-bold creator-notified" v-if="state.errorParams.creatorWasNotified">
							{{ state.errorParams.creatorWasNotified }}
						</p>

						<div v-if="showErrorDetails">
							<div class="error_block" v-if="state.errorParams.isStepError">
								<h3>There was an error in</h3>
								<div class="action_error">
									<div class="ic_action_type" style="position: relative; top: -1px">
										<LinkSVG />
									</div>
									<div class="error_app_icon_wrp">
										<div
											class="error_app_icon_image"
											:style="{ 'background-image': `url(${serviceIconURL(state.errorParams.serviceSlug)})` }"
										/>
									</div>
									<h3 class="action_title">{{ state.errorParams.actionTitle }}</h3>
								</div>
							</div>
							<div class="error_msg_block">
								<h3 class="mb-2 font-weight-bold">Error message:</h3>
								<p class="light">{{ state.errorParams.msg }}</p>
							</div>
						</div>
						<div v-else class="mt-8">The creator was notified</div>
						<div class="buttons_block">
							<a
								target="_blank"
								class="edit_btn"
								role="button"
								v-if="state.errorParams.editStepUrl"
								v-bind:href="state.errorParams.editStepUrl"
							>
								Edit Step </a
							><br />
							<button class="diss_btn" role="button" @click="setState({ isShow: false })">Dismiss</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { NodeRunStatuses } from '@ply-io/runner';

import ProgressBar from './ProgressBar.vue';
import LinkSVG from './assets/LinkSVG.vue';
import PlyLogoWithTextSVG from './assets/PlyLogoWithTextSVG.vue';
import NonExistantToolError from '@/components/common/WorkflowStatus/NonExistantToolError';
import IconService from '@/utils/icon-service';
import { WORKFLOW_ERROR_CODES } from '@/utils/workflow-service.const';
import { showFlowRunPercentage } from '@/views/FlowRun/flow-run-service';

const initialState = {
	text: '',
	isShow: false,
	percentCompleted: 0,
	runState: {
		// received from the server: ...dummyRunState
	},
	errorParams: {},
};

export default {
	name: 'WorkflowStatus',
	components: {
		NonExistantToolError,
		PlyLogoWithTextSVG,
		LinkSVG,
		ProgressBar,
	},
	props: {
		userIsOwner: { type: Boolean },
		showSimpleLoader: { type: Boolean },
		userInOrganization: { type: Boolean },
		hideErrorsFromOrganization: { type: Boolean },
		hideErrorsFromNonOrganization: { type: Boolean },
		fixedPosition: { type: Boolean, default: true },
		startMinimized: { type: Boolean, default: true },
		allowMinimize: { type: Boolean },
		allowStop: { type: Boolean },
		allowRestart: { type: Boolean },
		disableStop: { type: Boolean },
		disableRestart: { type: Boolean },
		forceNonExistantToolError: { type: Boolean, default: false },
	},
	expose: ['setState'],
	computed: {
		showProgressBar() {
			return showFlowRunPercentage(this.state.runState.status);
		},
		isError() {
			return Object.keys(this.state.errorParams).length > 0 || this.forceNonExistantToolError;
		},
		showNonExistantToolError() {
			return (
				this.forceNonExistantToolError || (this.isError && this.state.errorParams.code === WORKFLOW_ERROR_CODES.MODULE_NOT_FOUND_ERROR_CODE)
			);
		},
		showErrorDetails() {
			return (
				this.state?.errorParams?.forceShowError ||
				this.userIsOwner ||
				(this.userInOrganization ? !this.hideErrorsFromOrganization : !this.hideErrorsFromNonOrganization)
			);
		},
	},
	methods: {
		setState(state) {
			this.$set(this, 'state', { ...this.state, ...state });
			this.$nextTick(() => this.scrollToActiveNodeIndication());
		},
		scrollToActiveNodeIndication() {
			if (this.state.runState.nodeRuns?.length) {
				const activeNode = this.state.runState.nodeRuns.find((nr) => nr.status === NodeRunStatuses.Started);
				if (activeNode) {
					this.$refs.progressBar.scrollToNodeRun(activeNode.id);
				}
			}
		},
		serviceIconURL(serviceSlug) {
			return IconService.getExternalIconUrl(serviceSlug);
		},
	},
	data() {
		return {
			state: { ...initialState },
		};
	},
};
</script>

<style scoped lang="scss">
.workflow-status {
	$width: 268px;

	z-index: 2147483647;
	max-height: 100vh;
	height: auto;
	line-height: 1em !important;
	&.fixed {
		position: fixed;
		bottom: 30px;
		left: 10px;
	}

	.ply_error_cont {
		text-align: center;
		font-family: proxima-nova, ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, 'Apple Color Emoji', Arial,
			sans-serif, 'Segoe UI Emoji', 'Segoe UI Symbol' !important;
		h3 {
			font-size: 14px;
			font-weight: 400;
			font-family: proxima-nova, ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, 'Apple Color Emoji', Arial,
				sans-serif, 'Segoe UI Emoji', 'Segoe UI Symbol' !important;
			/* color: #1d2141; */
			//margin: 0px !important;
		}
		h4 {
			&.med {
				font-size: 12px;
				font-weight: 500;
				/* color: #1d2141; */
				font-family: proxima-nova, ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, 'Apple Color Emoji', Arial,
					sans-serif, 'Segoe UI Emoji', 'Segoe UI Symbol' !important;
				line-height: 24px !important;
				margin: 0px !important;
			}
			&.light {
				font-weight: 300;
				font-size: 12px;
				line-height: 1em !important;
				margin: 0px !important;
			}
		}
		.logo {
			display: inline-block;
			svg g g:first-child path {
				fill: #1d2141;
			}
		}
		.error_msg_title {
			font-size: 26px;
			font-weight: 700;
			margin-top: 25px;
		}
		.creator-notified {
			padding-top: 10px;
		}
		.error_block {
			position: relative;
			width: 100%;
			text-align: left;
			margin-top: 35px;
			.action_error {
				border-radius: 10px !important;
				background-color: var(--page-background);
				border: 1px solid #e4e8f2;
				width: 100%;
				height: 37px;
				line-height: 37px;
				margin-top: 7px;
				.ic_action_type {
					position: relative;
					display: inline-block;
					vertical-align: middle;
					margin-left: 10px;
				}
				.error_app_icon_wrp {
					width: 20px;
					height: 20px;
					border-radius: 4px;
					border: 1px solid #e4e8f2;
					position: relative;
					display: inline-block;
					vertical-align: middle;
					margin: 0px 10px;
					margin-right: 6px;
					.error_app_icon_image {
						position: absolute;
						top: 50%;
						left: 50%;
						transform: translate(-50%, -50%);
						background-repeat: no-repeat;
						background-size: contain;
						background-position: center center;
						width: 14px;
						height: 20px;
					}
				}
				.action_title {
					position: relative;
					display: inline-block;
					vertical-align: middle;
				}
			}
			svg {
				g {
					stroke: #e4e8f2 !important;
				}
				path {
					stroke: #e4e8f2 !important;
				}
			}
		}
		.step_block {
			position: relative;
			width: 100%;
			text-align: left;
			margin-top: 30px;
		}
		.error_msg_block {
			text-align: left;
			margin-top: 30px;
			max-height: 60px;
		}
	}
	.progress_wrp {
		margin: 5px 0px;
		width: $width;
		background: #ffffff;
		border: 1px solid #ecedf2;
		border-radius: 12px;
		box-shadow: 0 3px 14px -3px #5d90f4;
		position: relative;
		display: block;
		z-index: 2147483647;
		box-sizing: border-box;
		font-family: proxima-nova, ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, 'Apple Color Emoji', Arial,
			sans-serif, 'Segoe UI Emoji', 'Segoe UI Symbol' !important;
		transition: height 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
		.err {
			visibility: hidden;
			opacity: 0;
			transition: all 0.25s;
			.buttons_block {
				position: absolute;
				width: 100%;
				text-align: center;
				bottom: 5px;
				left: 0px;
				.edit_btn {
					background: #703ff1 !important;
					width: 150px !important;
					height: auto !important;
					display: inline-block !important;
					cursor: pointer;
					transition: all 0.2s;
					border: none;
					border-radius: 5px;
					position: static !important;
					outline: none !important;
					color: white !important;
					font-size: 14px;
					padding: 10px 0;

					display: inline-block;
					text-decoration: none;
					vertical-align: middle;
					&:hover {
						filter: brightness(0.85);
					}
				}
				.diss_btn {
					background: transparent;
					color: #703ff1;
					height: 40px;
					width: 150px;
					cursor: pointer;
					transition: all 0.2s;
					border: none;
					border-radius: 5px;
					position: relative;
					display: inline-block;
					outline: none !important;
					font-size: 14px;
					&:hover {
						color: #4679e5;
					}
				}
			}
		}

		&.pb_show_error_msg {
			width: 400px;
			height: 445px;
			color: #1d2141 !important;
			padding: 20px;
			cursor: auto;
			.err {
				visibility: visible;
				opacity: 1;
			}
		}
	}
}
</style>
